import { useState, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

/**
 * 海报生成 Hook
 * 负责生成二维码、截图海报、下载图片
 */
export function usePosterGenerator() {
    const [isGenerating, setIsGenerating] = useState(false);
    const [posterDataUrl, setPosterDataUrl] = useState(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
    const [error, setError] = useState(null);
    const posterRef = useRef(null);

    /**
     * 生成裂变链接
     * @param {string} userId - 用户ID
     * @param {string} configId - 配置ID（可选）
     * @returns {string} 完整的分享链接
     */
    const generateShareLink = useCallback((userId, configId = null) => {
        const baseUrl = window.location.origin;
        const params = new URLSearchParams();

        if (userId) {
            params.set('ref', userId);
        }
        params.set('source', 'poster');

        if (configId) {
            return `${baseUrl}/gallery/${configId}?${params.toString()}`;
        }

        return `${baseUrl}/configurator?${params.toString()}`;
    }, []);

    /**
     * 生成二维码
     * @param {string} url - 要编码的URL
     * @returns {Promise<string>} 二维码的 Data URL
     */
    const generateQRCode = useCallback(async (url) => {
        try {
            const qrDataUrl = await QRCode.toDataURL(url, {
                width: 200,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'M'
            });
            setQrCodeDataUrl(qrDataUrl);
            return qrDataUrl;
        } catch (err) {
            console.error('QR Code generation failed:', err);
            setError('二维码生成失败');
            return null;
        }
    }, []);

    /**
     * 生成海报图片
     * @param {HTMLElement} element - 要截图的DOM元素
     * @returns {Promise<string>} 海报的 Data URL
     */
    const generatePoster = useCallback(async (element) => {
        if (!element) {
            setError('海报元素未找到');
            return null;
        }

        setIsGenerating(true);
        setError(null);

        try {
            // 等待图片加载完成
            const images = element.querySelectorAll('img');
            await Promise.all(
                Array.from(images).map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });
                })
            );

            const canvas = await html2canvas(element, {
                scale: 2, // 2倍分辨率，保证清晰度
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                logging: false,
                width: element.offsetWidth,
                height: element.offsetHeight
            });

            const dataUrl = canvas.toDataURL('image/png', 1.0);
            setPosterDataUrl(dataUrl);
            return dataUrl;
        } catch (err) {
            console.error('Poster generation failed:', err);
            setError('海报生成失败，请重试');
            return null;
        } finally {
            setIsGenerating(false);
        }
    }, []);

    /**
     * 下载海报图片
     * @param {string} dataUrl - 图片的 Data URL
     * @param {string} filename - 文件名
     */
    const downloadPoster = useCallback((dataUrl, filename = 'my-fatboy-config.png') => {
        if (!dataUrl) {
            setError('请先生成海报');
            return;
        }

        try {
            const link = document.createElement('a');
            link.download = filename;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Download failed:', err);
            setError('下载失败，请重试');
        }
    }, []);

    /**
     * 复制分享链接到剪贴板
     * @param {string} url - 要复制的URL
     * @returns {Promise<boolean>} 是否成功
     */
    const copyShareLink = useCallback(async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            return true;
        } catch (err) {
            console.error('Copy failed:', err);
            // 降级方案
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                return true;
            } catch (e) {
                setError('复制失败');
                return false;
            } finally {
                document.body.removeChild(textArea);
            }
        }
    }, []);

    /**
     * 分享到社交媒体（使用 Web Share API）
     * @param {object} shareData - 分享数据
     * @returns {Promise<boolean>} 是否成功
     */
    const shareToSocial = useCallback(async (shareData) => {
        const { title, text, url, files } = shareData;

        // 检查是否支持 Web Share API
        if (navigator.share) {
            try {
                const data = { title, text, url };

                // 如果支持文件分享且有文件
                if (files && navigator.canShare && navigator.canShare({ files })) {
                    data.files = files;
                }

                await navigator.share(data);
                return true;
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                }
                return false;
            }
        }

        // 不支持 Web Share API
        return false;
    }, []);

    /**
     * 重置状态
     */
    const reset = useCallback(() => {
        setPosterDataUrl(null);
        setQrCodeDataUrl(null);
        setError(null);
        setIsGenerating(false);
    }, []);

    return {
        // 状态
        isGenerating,
        posterDataUrl,
        qrCodeDataUrl,
        error,
        posterRef,

        // 方法
        generateShareLink,
        generateQRCode,
        generatePoster,
        downloadPoster,
        copyShareLink,
        shareToSocial,
        reset
    };
}
